import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { api, ApiError, AUTH_EXPIRED_EVENT } from './api'
import type { User } from './types'

type AuthValue = {
  user: User | null
  loading: boolean
  login(email: string, password: string): Promise<void>
  verify(code: string): Promise<void>
  verifyRecovery(code: string): Promise<void>
  logout(): Promise<void>
  refresh(): Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = async () => {
    try {
      const result = await api<{ user: User }>('/auth/session')
      setUser(result.user)
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) console.error(error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  useEffect(() => {
    const expired = () => setUser(null)
    window.addEventListener(AUTH_EXPIRED_EVENT, expired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, expired)
  }, [])

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      refresh,
      async login(email, password) {
        await api('/auth/login', { method: 'POST', json: { email, password } })
      },
      async verify(code) {
        const result = await api<{ user: User }>('/auth/totp/verify', {
          method: 'POST',
          json: { code },
        })
        setUser(result.user)
      },
      async verifyRecovery(code) {
        const result = await api<{ user: User }>('/auth/recovery/verify', {
          method: 'POST',
          json: { code },
        })
        setUser(result.user)
      },
      async logout() {
        try {
          await api('/auth/logout', { method: 'POST', json: {} })
        } catch (error) {
          console.warn('Server logout failed; local session was cleared.', error)
        } finally {
          setUser(null)
        }
      },
    }),
    [loading, user],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('AuthProvider is missing')
  return value
}
